import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import logoCrcUrl from '../assets/logoCrc.png';

/**
 * Colors aligned with Spacer Log HTML brief template
 * (SpacerLogDashboard.css --sl-* + Asset IQ header accents).
 */
const COLORS = {
  ink: rgb(0.2, 0.2, 0.2), // #333 — body / headings
  muted: rgb(0.4, 0.4, 0.4), // #666
  mutedDim: rgb(0.6, 0.6, 0.6), // #999 — eyebrow
  accent: rgb(0, 0.659, 0.557), // #00a88e — quote bar
  red: rgb(0.769, 0.118, 0.118), // #c41e1e — header rule (PDF btn)
  ruleSoft: rgb(0.851, 0.882, 0.906), // #d9e1e7
};

const PAGE = { width: 595.28, height: 841.89 }; // A4
const MARGIN = { left: 54, right: 54, top: 48, bottom: 54 };
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;

/** Helvetica/WinAnsi-safe text (AI briefs often include arrows, emoji, etc.) */
export const sanitizePdfText = (input) =>
  String(input ?? '')
    .normalize('NFKC')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u2022\u25CF\u25E6\u00B7]/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2190-\u21FF]/g, '->')
    .replace(/[^\t\n\r\x20-\x7E\xA0-\xFF]/g, '');

const stripInlineMd = (text) =>
  sanitizePdfText(
    String(text || '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
  );

/**
 * Parse the same lightweight markdown subset as SpacerLogMarkdown
 * into typed blocks for PDF layout.
 */
export const parseMarkdownBlocks = (markdown) => {
  const raw = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!raw) return [];

  const lines = raw.split('\n');
  const blocks = [];
  let listBuf = [];
  let listOrdered = false;

  const flushList = () => {
    if (!listBuf.length) return;
    blocks.push({ type: listOrdered ? 'ol' : 'ul', items: listBuf.slice() });
    listBuf = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    const ul = trimmed.match(/^[-*]\s+(.+)$/);
    const ol = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ul) {
      if (listBuf.length && listOrdered) flushList();
      listOrdered = false;
      listBuf.push(stripInlineMd(ul[1]));
      return;
    }
    if (ol) {
      if (listBuf.length && !listOrdered) flushList();
      listOrdered = true;
      listBuf.push(stripInlineMd(ol[1]));
      return;
    }

    flushList();

    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: stripInlineMd(trimmed.slice(4)) });
      return;
    }
    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: stripInlineMd(trimmed.slice(3)) });
      return;
    }
    if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'h1', text: stripInlineMd(trimmed.slice(2)) });
      return;
    }
    if (trimmed.startsWith('> ')) {
      blocks.push({ type: 'quote', text: stripInlineMd(trimmed.slice(2)) });
      return;
    }

    blocks.push({ type: 'p', text: stripInlineMd(trimmed) });
  });

  flushList();
  return blocks;
};

const wrapText = (text, font, size, maxWidth) => {
  const words = sanitizePdfText(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      if (font.widthOfTextAtSize(word, size) <= maxWidth) {
        current = word;
      } else {
        let rest = word;
        while (rest.length) {
          let i = 1;
          while (
            i <= rest.length
            && font.widthOfTextAtSize(rest.slice(0, i), size) <= maxWidth
          ) {
            i += 1;
          }
          const cut = Math.max(1, i - 1);
          lines.push(rest.slice(0, cut));
          rest = rest.slice(cut);
        }
        current = '';
      }
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

const formatBriefDate = (date = new Date()) => {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

const safeDrawText = (page, text, opts) => {
  const clean = sanitizePdfText(text);
  if (!clean) return;
  try {
    page.drawText(clean, opts);
  } catch (err) {
    // Last resort: strip to ASCII
    const ascii = clean.replace(/[^\x20-\x7E]/g, '');
    if (ascii) page.drawText(ascii, opts);
  }
};

/**
 * Build Ask Evans PDF matching Asset IQ header + Spacer Log HTML body colours.
 * @returns {Promise<Blob>}
 */
export const buildAskEvansPdfReport = async ({
  markdown,
  title = 'Ask Evans Report',
  eyebrow = 'SPACER LOG - ASK EVANS REPORT',
  subtitle = 'Prepared for operations - CONFIDENTIAL',
  question,
} = {}) => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  let logoImage = null;
  try {
    const logoRes = await fetch(logoCrcUrl);
    if (logoRes.ok) {
      const logoBytes = await logoRes.arrayBuffer();
      logoImage = await pdf.embedPng(logoBytes);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Ask Evans PDF: logo unavailable', err);
  }

  const blocks = parseMarkdownBlocks(markdown);
  const pageBlocks = [];

  if (question && String(question).trim()) {
    pageBlocks.push({ type: 'p', text: `Question: ${stripInlineMd(question)}` });
  }
  pageBlocks.push(...blocks);

  let page = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN.top;

  const drawHeader = (targetPage, { skipTitle = false } = {}) => {
    const headerY = PAGE.height - MARGIN.top;
    const eye = sanitizePdfText(String(eyebrow || '').toUpperCase());

    safeDrawText(targetPage, eye, {
      x: MARGIN.left,
      y: headerY - 4,
      size: 8,
      font,
      color: COLORS.mutedDim,
    });

    if (logoImage) {
      const maxW = 120;
      const maxH = 20;
      const scale = Math.min(maxW / logoImage.width, maxH / logoImage.height);
      const w = logoImage.width * scale;
      const h = logoImage.height * scale;
      targetPage.drawImage(logoImage, {
        x: PAGE.width - MARGIN.right - w,
        y: headerY - h + 2,
        width: w,
        height: h,
      });
    } else {
      const brandSize = 11;
      const crcW = fontBold.widthOfTextAtSize('CRC', brandSize);
      const evansW = font.widthOfTextAtSize('EVANS', brandSize);
      safeDrawText(targetPage, 'EVANS', {
        x: PAGE.width - MARGIN.right - evansW,
        y: headerY - 2,
        size: brandSize,
        font,
        color: COLORS.muted,
      });
      safeDrawText(targetPage, 'CRC', {
        x: PAGE.width - MARGIN.right - evansW - 3 - crcW,
        y: headerY - 2,
        size: brandSize,
        font: fontBold,
        color: COLORS.red,
      });
    }

    // Gap below logo / eyebrow before the red rule
    const ruleY = headerY - 30;
    targetPage.drawLine({
      start: { x: MARGIN.left, y: ruleY },
      end: { x: PAGE.width - MARGIN.right, y: ruleY },
      thickness: 1.5,
      color: COLORS.red,
    });

    if (skipTitle) return ruleY - 16;

    let cursor = ruleY - 26;
    const dateLine = formatBriefDate();
    const titleBase = sanitizePdfText(String(title || 'Ask Evans Report').trim());
    const titleText = titleBase.includes(dateLine)
      ? titleBase
      : `${titleBase} - ${dateLine}`;
    wrapText(titleText, fontBold, 14, CONTENT_WIDTH).forEach((line) => {
      safeDrawText(targetPage, line, {
        x: MARGIN.left,
        y: cursor,
        size: 14,
        font: fontBold,
        color: COLORS.ink,
      });
      cursor -= 18;
    });

    const sub = sanitizePdfText(
      String(subtitle || 'Prepared for operations - CONFIDENTIAL').trim()
    );
    wrapText(sub, font, 10, CONTENT_WIDTH).forEach((line) => {
      safeDrawText(targetPage, line, {
        x: MARGIN.left,
        y: cursor,
        size: 10,
        font,
        color: COLORS.muted,
      });
      cursor -= 14;
    });

    return cursor - 10;
  };

  const ensureSpace = (needed) => {
    if (y - needed >= MARGIN.bottom) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    drawHeader(page, { skipTitle: true });
    y = PAGE.height - MARGIN.top - 52;
  };

  y = drawHeader(page, { skipTitle: false });

  const drawWrapped = (text, { size, bold = false, color = COLORS.ink, lineGap = 3, indent = 0 } = {}) => {
    const useFont = bold ? fontBold : font;
    const maxW = CONTENT_WIDTH - indent;
    const lines = wrapText(text, useFont, size, maxW);
    const lineHeight = size + lineGap;
    lines.forEach((line) => {
      ensureSpace(lineHeight + 2);
      safeDrawText(page, line, {
        x: MARGIN.left + indent,
        y,
        size,
        font: useFont,
        color,
      });
      y -= lineHeight;
    });
  };

  pageBlocks.forEach((block) => {
    if (block.type === 'h1') {
      y -= 10;
      ensureSpace(28);
      drawWrapped(block.text, { size: 13, bold: true, lineGap: 4 });
      y -= 2;
      // Soft rule under major headings (HTML h2 weight)
      ensureSpace(8);
      page.drawLine({
        start: { x: MARGIN.left, y: y + 4 },
        end: { x: MARGIN.left + 80, y: y + 4 },
        thickness: 1,
        color: COLORS.accent,
      });
      y -= 6;
      return;
    }
    if (block.type === 'h2') {
      y -= 12;
      ensureSpace(26);
      drawWrapped(block.text, { size: 12, bold: true, lineGap: 4 });
      y -= 4;
      return;
    }
    if (block.type === 'h3') {
      y -= 8;
      ensureSpace(22);
      drawWrapped(block.text, { size: 11, bold: true, lineGap: 3 });
      y -= 2;
      return;
    }
    if (block.type === 'quote') {
      y -= 4;
      ensureSpace(20);
      page.drawRectangle({
        x: MARGIN.left,
        y: y - 2,
        width: 3,
        height: 14,
        color: COLORS.accent,
      });
      drawWrapped(block.text, {
        size: 10,
        color: COLORS.muted,
        lineGap: 3,
        indent: 12,
      });
      y -= 4;
      return;
    }
    if (block.type === 'ul' || block.type === 'ol') {
      y -= 2;
      block.items.forEach((item, i) => {
        const bullet = block.type === 'ol' ? `${i + 1}.` : '-';
        ensureSpace(16);
        safeDrawText(page, bullet, {
          x: MARGIN.left,
          y,
          size: 10,
          font,
          color: COLORS.ink,
        });
        const prefixW = font.widthOfTextAtSize(`${bullet}  `, 10);
        wrapText(item, font, 10, CONTENT_WIDTH - prefixW).forEach((line, li) => {
          if (li > 0) ensureSpace(14);
          safeDrawText(page, line, {
            x: MARGIN.left + prefixW,
            y,
            size: 10,
            font,
            color: COLORS.ink,
          });
          y -= 13;
        });
      });
      y -= 4;
      return;
    }
    y -= 2;
    drawWrapped(block.text, { size: 10.5, lineGap: 4 });
    y -= 6;
  });

  const bytes = await pdf.save();
  // Copy into a plain ArrayBuffer so Blob works in all browsers
  const copy = new Uint8Array(bytes);
  return new Blob([copy.buffer], { type: 'application/pdf' });
};
